/**
 * Authentication and Authorization Type Definitions
 * 
 * This file contains all TypeScript types, interfaces, and enums related to
 * authentication and authorization in the application.
 */

import Account from "@/features/account/application/domain/entities/account";
import { AppUser } from "@/features/app-user/application/domain/entities/account";
import { AppUserDtoResponse } from "@/features/app-user/infra/dto/appUser.res.dto";


/**
 * Permission saparator
 */
export const PERMISSION_SEPARATOR = '.';

// ============================================================================
// Enums for Type-Safe Permission Management
// ============================================================================

/**
 * Available resources in the application
 * Used to construct permission strings with actions
 */

export enum Resource {
    DASHBOARD = 'dashboard',
    ROLES = 'roles',
    USERS = 'users',
    EMPLOYEES = 'employees',
    REMOTE_VERIFIERS = 'remoteVerifiers',
    DATA_SYNC = 'dataSync',
    SECTION_1 = 'section1',
    SUPPLEMENT_A = 'supplementA',
    SSN_ONLY_SECTION_1 = 'ssnOnlySection1',
    SECTION_2 = 'section2',
    DOCUMENTS = 'documents',
    SUPPLEMENT_B = 'supplementB',
    AUDIT_REPORT = 'auditReport',
    EVERIFY_FLAG = 'eVerifyFlag',
    CREDENTIAL_MANAGER_EVERIFY_DHS = 'credentialManagerEVerifyDhs',
    EVERIFY = 'eVerify',
    PHOTO_MATCHING = 'photoMatching',
    TNC = 'tnc',
    DHS_SSA_COMMUNICATION = 'dhsSsaCommunication',
    EVERIFY_HISTORY = 'eVerifyHistory',
    REPORTS = 'reports',
    FORM_I9_STORAGE = 'formI9Storage',
    FORM_I9_DIGITIZATION = 'formI9Digitization',
    FORM_I9_REMEDIATION = 'formI9Remediation',
    REMEDIATION_REPORT = 'remediationReport',
    COMPLIANCE = 'compliance',
    NOTIFICATIONS = 'notifications',
    ONBOARDING = 'onboarding',
    SETTINGS = 'settings',
}

/**
 * Available actions that can be performed on resources
 * Used to construct permission strings with resources
 */
export enum Action {
    READ = 'read',
    CREATE = 'create',
    UPDATE = 'update',
    DELETE = 'delete',
}

// ============================================================================
// Permission Types
// ============================================================================

/**
 * Permission string in the format "resource:action"
 * Examples: "dashboard:read", "section2:edit"
 */
export type Permission = `${Resource}:${Action}`;

// ============================================================================
// User & Auth State Types
// ============================================================================

/**
 * User profile information
 */
export interface User {
    userId: string;
    username: string;
    email: string;
    role: string; // e.g., "admin", "manager", "employee"
}

/**
 * Redux auth state shape
 */
export interface AuthState {
  user: AppUser | null;
  account: Account | null;
  role: string | null;
  permissions: Permission[];
  isAuthenticated: boolean;
  hasSelectedAccount: boolean;
  loading: boolean;
  error: string | null;
  isRemoteI9User: boolean;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Response from /auth endpoint (user profile with permissions)
 */
export interface AuthResponse {
    accessToken: string
    appUser: AppUserDtoResponse
    roleKey: string
    permissions: string[]
}



// ============================================================================
// Mock Role Selection Types (for testing)
// ============================================================================

/**
 * Predefined roles available for mock testing
 */
export type PredefinedRole = 'admin' | 'hr' | 'everify_expert' | 'custom';

/**
 * Role definition with name and permissions
 */
export interface RoleDefinition {
    id: PredefinedRole;
    name: string;
    displayName: string;
    description: string;
    permissions: Permission[];
    color: string; // For UI styling
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Create a permission string from resource and action enums
 * Provides type safety and autocomplete
 * 
 * @param resource - The resource enum value
 * @param action - The action enum value
 * @returns Permission string in format "resource:action"
 * 
 * @example
 * const permission = createPermission(Resource.SECTION2, Action.EDIT);
 * // Returns: "section2:edit"
 */
export function createPermission(resource: Resource, action: Action): Permission {
    return `${resource}${PERMISSION_SEPARATOR}${action}` as Permission;
}

/**
 * Parse a permission string into its resource and action components
 * 
 * @param permission - The permission string to parse
 * @returns Object containing resource and action strings
 * 
 * @example
 * const { resource, action } = parsePermission("section2:edit");
 * // Returns: { resource: "section2", action: "edit" }
 */
export function parsePermission(permission: Permission): { resource: string; action: string } {
    const [resource, action] = permission.split(PERMISSION_SEPARATOR);
    return { resource, action };
}

/**
 * Check if a permission string is valid
 * 
 * @param permission - String to validate
 * @returns True if the string is a valid permission format
 */
export function isValidPermission(permission: string): permission is Permission {
    const parts = permission.split(PERMISSION_SEPARATOR);
    if (parts.length !== 2) return false;

    const [resource, action] = parts;
    return (
        Object.values(Resource).includes(resource as Resource) &&
        Object.values(Action).includes(action as Action)
    );
}
